import { useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { Button, Stack, Tooltip } from '@evaneos/b2b-ui';
import ArrowBack from '@evaneos/b2b-ui/icons/ArrowBack';
import LandscapeIcon from '@evaneos/b2b-ui/icons/LandscapeOutlined';
import EuroIcon from '@evaneos/b2b-ui/icons/Euro';
import ReopenIcon from '@evaneos/b2b-ui/icons/UTurnRight';
import { useIntl } from 'react-intl';

import { DEFAULT_VIEW_ROUTE } from '../../../shared/routing/Routes';
import { DossierId } from '../../../resources/dossier/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../shared/analytics';
import { useSidePanel } from '../SidePanel/SidePanelContext';
import { useDossierLayoutInfo } from '../DossierLayoutInfoContext';
import { LOST_STEP } from '../../../domain';
import { useReopenMutation } from '../../../resources/dossier/mutations';
import { useCurrentTaskByDossierId } from '../../../resources/dossier/queries';

import { MoreOptionsMenu } from './MoreOptionsMenu';

export const DossierNavbar = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const { trackEvent } = useTrackEventAmplitude();
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { step } = useCurrentTaskByDossierId(dossierId) || {};
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const hasNavigatedInRequestManager = navigationType !== 'POP';
    const { openPanel } = useSidePanel();
    const { isFirstColumnNarrow } = useDossierLayoutInfo();
    const reopenMutation = useReopenMutation(dossierId);

    const isMinified = isFirstColumnNarrow || step === LOST_STEP;

    const handleReopen = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.reopenFile({ ...defaultProperties, ...trackedTripProperties });
        });
        reopenMutation.mutate();
    };

    const handleBackClick = () => {
        if (!hasNavigatedInRequestManager) {
            navigate(DEFAULT_VIEW_ROUTE);
        } else {
            navigate(-1);
        }
    };

    const handleItineraryClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.itineraryClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={2}>
                <Tooltip
                    title={formatMessage({ id: 'global.back' })}
                    disableHoverListener={!isMinified}
                >
                    <span>
                        <Button
                            variant="outlined"
                            onClick={handleBackClick}
                            startIcon={isMinified ? undefined : <ArrowBack />}
                            sx={
                                isMinified
                                    ? {
                                          p: 1,
                                          minWidth: 0,
                                          width: '40px',
                                          height: '40px',
                                      }
                                    : undefined
                            }
                        >
                            {isMinified ? <ArrowBack /> : formatMessage({ id: 'global.back' })}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="flex-start" gap={1}>
                <Tooltip
                    title={formatMessage({ id: 'dossier.actions.quote' })}
                    disableHoverListener={!isMinified}
                >
                    <Button
                        component="a"
                        href={`/trip-planner/trip?request_id=${dossierId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        onClick={handleItineraryClick}
                        startIcon={isMinified ? undefined : <LandscapeIcon />}
                        sx={
                            isMinified
                                ? {
                                      p: 1,
                                      minWidth: 0,
                                      width: '40px',
                                      height: '40px',
                                  }
                                : undefined
                        }
                    >
                        {isMinified ? (
                            <LandscapeIcon />
                        ) : (
                            formatMessage({ id: 'dossier.actions.quote' })
                        )}
                    </Button>
                </Tooltip>
                <Tooltip
                    title={formatMessage({ id: 'contract.titles.contract' })}
                    disableHoverListener={!isMinified}
                >
                    <Button
                        variant="outlined"
                        onClick={() => openPanel({ contentType: 'contract' })}
                        startIcon={isMinified ? undefined : <EuroIcon />}
                        sx={
                            isMinified
                                ? {
                                      p: 1,
                                      minWidth: 0,
                                      width: '40px',
                                      height: '40px',
                                  }
                                : undefined
                        }
                    >
                        {isMinified ? (
                            <EuroIcon />
                        ) : (
                            formatMessage({ id: 'contract.titles.contract' })
                        )}
                    </Button>
                </Tooltip>
                {step === LOST_STEP ? (
                    <Button
                        startIcon={<ReopenIcon sx={{ transform: 'rotate(-90deg)' }} />}
                        onClick={handleReopen}
                    >
                        {formatMessage({ id: 'dossier.reopen' })}
                    </Button>
                ) : (
                    <MoreOptionsMenu />
                )}
            </Stack>
        </Stack>
    );
};
